import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import t from 'react-translate';

import { CourseRoleplayApproval, RolePlayInteractionMode } from 'redux/schemas/models/ai-role-play';
import { RoleplayApprovalMode } from 'redux/schemas/models/institution';
import { RoleplayUsageValue } from 'roleplay/course-level-management/use-roleplay-usage-form';
import { addAlertMessage } from './alert-messages';
import { AlertMessageType } from '../schemas/app/alert-message';

export const toUsageValue = (approval: CourseRoleplayApproval): RoleplayUsageValue => ({
  enabled: approval.enabled,
  approvedTypes: [
    ...(approval.textEnabled ? [RolePlayInteractionMode.TEXT] : []),
    ...(approval.voiceEnabled ? [RolePlayInteractionMode.VOICE] : []),
  ],
});

export const toApprovalPayload = (value: RoleplayUsageValue): CourseRoleplayApproval => ({
  enabled: value.enabled,
  // Disabled always sends both flags off to avoid contradictory payloads.
  textEnabled: value.enabled && value.approvedTypes.includes(RolePlayInteractionMode.TEXT),
  voiceEnabled: value.enabled && value.approvedTypes.includes(RolePlayInteractionMode.VOICE),
});

/** Types to display for a course; none when roleplay is off, so stale type flags never imply approval. */
export const approvedRoleplayTypesFor = (
  settings?: CourseRoleplayApproval,
): RolePlayInteractionMode[] => (
  settings?.enabled ? toUsageValue(settings).approvedTypes : []
);

/** Absent settings mean no approval yet, so the type stays blocked. */
export const isRoleplayTypeApproved = (
  settings: CourseRoleplayApproval | undefined,
  interactionMode: RolePlayInteractionMode,
): boolean => approvedRoleplayTypesFor(settings).includes(interactionMode);

export const getCourseRoleplayUsage = createAsyncThunk<RoleplayUsageValue, string>(
  'GET_COURSE_ROLEPLAY_USAGE',
  async (catalogId, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.get(`/courses/${catalogId}/ai_role_play_course_approval.json`);
      return toUsageValue(response.data.result);
    } catch (error) {
      dispatch(addAlertMessage({
        type: AlertMessageType.ERROR,
        header: t.FORM.ERROR(),
      }));
      return rejectWithValue(error);
    }
  },
);

export const setAiRolePlayCourseAccessSetting = createAsyncThunk(
  'SET_AI_ROLE_PLAY_COURSE_ACCESS_SETTING',
  (params: { institutionId: number, setting: RoleplayApprovalMode }) => axios.put(
    `/institutions/${params.institutionId}/ai_role_play_course_access_setting.json`,
    {
      aiRolePlayCourseAccessSetting: params.setting,
    },
  ).then((response) => response.data.result),
);

export const setCourseRoleplayUsage = createAsyncThunk<
RoleplayUsageValue,
{ catalogId: string, value: RoleplayUsageValue }
>(
  'SET_COURSE_ROLEPLAY_USAGE',
  async ({ catalogId, value }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.post(`/courses/${catalogId}/ai_role_play_course_approval.json`, {
        aiRolePlayAccessSettings: toApprovalPayload(value),
      });
      return toUsageValue(response.data.result);
    } catch (error) {
      dispatch(addAlertMessage({
        type: AlertMessageType.ERROR,
        header: t.FORM.ERROR(),
      }));
      return rejectWithValue(error);
    }
  },
);
